import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  FormGroup,
  ButtonGroup
} from '@mui/material';
import { audioService, musicTracks } from '../services/AudioService';

/**
 * Test component for audio playback simulation
 */
const AudioTest: React.FC = () => {
  const [currentTrack, setCurrentTrack] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<{[key: string]: string}>({});
  const [diagnosticResults, setDiagnosticResults] = useState<{[key: string]: string}>({});
  const [usingSilentMode, setUsingSilentMode] = useState(false);

  // Handle play button
  const handlePlay = (trackId: string) => {
    setLoading(true);
    setError(null);
    setCurrentTrack(trackId);
    
    // Check if track exists
    const track = audioService.getTrackById(trackId);
    if (!track) {
      setError(`Track not found: ${trackId}`);
      setLoading(false);
      return;
    }
    
    // Stop any playing track
    if (isPlaying && currentTrack) {
      audioService.stopTrack(currentTrack);
    }
    
    // Set result to testing
    setTestResults(prev => ({
      ...prev,
      [trackId]: 'Testing...'
    }));
    
    // Play the track
    audioService.getAudio(trackId)
      .then(audio => {
        // Check if we're using fallback
        const state = audioService.getLoadingState(trackId);
        const isFallback = state.usingSilentFallback;
        
        return audioService.playTrack(trackId)
          .then(() => {
            setIsPlaying(true);
            setLoading(false);
            setTestResults(prev => ({
              ...prev,
              [trackId]: isFallback 
                ? '⚠️ Using silent audio (fallback)' 
                : '✅ Real audio playback successful'
            }));
          });
      })
      .catch(err => {
        setIsPlaying(false);
        setLoading(false);
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(`Failed to play: ${errorMessage}`);
        setTestResults(prev => ({
          ...prev,
          [trackId]: `❌ Error: ${errorMessage}`
        }));
      });
  };

  // Handle stop button
  const handleStop = () => {
    if (currentTrack) {
      audioService.stopTrack(currentTrack);
      setIsPlaying(false);
      setCurrentTrack(null);
      setTestResults(prev => ({
        ...prev,
        [currentTrack]: prev[currentTrack] + ' (stopped)'
      }));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentTrack) {
        audioService.stopTrack(currentTrack);
      }
      audioService.clearCache();
    };
  }, [currentTrack]);

  // Toggle silent mode
  const toggleSilentMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const silent = event.target.checked;
    setUsingSilentMode(silent);
    audioService.forceSilentMode(silent);
    
    // Clear test results when toggling
    setTestResults({});
    
    // If playing, stop it
    if (isPlaying && currentTrack) {
      audioService.stopTrack(currentTrack);
      setIsPlaying(false);
      setCurrentTrack(null);
    }
    
    // Clear cache
    audioService.clearCache();
    
    // Run diagnostics again
    runDiagnostics();
  };

  // Run diagnostics
  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Run service diagnostics
      const results = await audioService.runDiagnostics();
      setDiagnosticResults(results);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError("Error running diagnostics: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Card sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>Audio Testing Tool</Typography>
          <Typography variant="body1" paragraph>
            Use this tool to test audio playback, diagnose issues, and configure audio settings.
          </Typography>
          
          <FormGroup sx={{ mb: 2 }}>
            <FormControlLabel 
              control={
                <Switch 
                  checked={usingSilentMode} 
                  onChange={toggleSilentMode} 
                />
              } 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>Silent Mode</Typography>
                  {usingSilentMode && (
                    <Chip 
                      label="Enabled" 
                      color="warning" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              }
            />
          </FormGroup>
          
          <Alert severity={usingSilentMode ? "warning" : "info"} sx={{ mb: 2 }}>
            {usingSilentMode 
              ? "Silent mode is enabled. No audio will be heard, but UI will respond as if music is playing."
              : "Real audio mode is enabled. The system will attempt to play actual audio files with silent fallback if needed."
            }
          </Alert>
        </CardContent>
      </Card>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <ButtonGroup sx={{ mb: 3 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={runDiagnostics}
          disabled={loading}
          size="large"
        >
          {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
          Run Audio Diagnostics
        </Button>
        
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => audioService.clearCache()}
          size="large"
        >
          Clear Audio Cache
        </Button>
      </ButtonGroup>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Diagnostic Results</Typography>
        <Divider sx={{ mb: 2 }} />
        
        {Object.keys(diagnosticResults).length > 0 ? (
          <Box>
            {Object.entries(diagnosticResults).map(([key, result]) => (
              <Typography key={key} sx={{ mb: 1 }}>
                <strong>{key}:</strong> {result}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">
            No diagnostics run yet. Click the button above to test audio.
          </Typography>
        )}
      </Paper>
      
      <Typography variant="h6" gutterBottom>Playback Tests</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {musicTracks.map(track => (
          <Paper key={track.id} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6">{track.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {track.artist} • {track.genre} • {track.duration}
                </Typography>
              </Box>
              {testResults[track.id] && (
                <Chip 
                  label={testResults[track.id]} 
                  color={
                    testResults[track.id].includes('✅') ? 'success' : 
                    testResults[track.id].includes('⚠️') ? 'warning' : 'error'
                  }
                  sx={{ fontSize: '0.8rem' }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => handlePlay(track.id)}
                disabled={loading || (isPlaying && currentTrack === track.id)}
              >
                {loading && currentTrack === track.id ? (
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                ) : null}
                {usingSilentMode ? "Simulate Play" : "Play Audio"}
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleStop}
                disabled={!isPlaying || currentTrack !== track.id}
              >
                {usingSilentMode ? "Stop Simulation" : "Stop Audio"}
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default AudioTest; 